/**
 * @file expenseCategories.js
 * @description Fuente única de las 10 categorías de gasto base + helpers para mostrarlas y
 * combinarlas con las categorías personalizadas del negocio (tabla `expense_categories`).
 *
 * El valor guardado en `expenses.category` es la CLAVE en inglés para las base
 * (`'Utilities'`, `'Rent'`, …) y el NOMBRE tal cual para las personalizadas (`'Catering'`).
 */

export const BUILTIN_EXPENSE_CATEGORY_KEYS = [
    'Utilities',
    'Products',
    'Rent',
    'Payroll',
    'Administrative',
    'Marketing',
    'Transport',
    'Maintenance',
    'Equipment',
    'Other',
];

/**
 * Etiqueta visible para CUALQUIER valor guardado: clave base → traducción; nombre
 * personalizado o valor histórico sin traducción → el string tal cual.
 *
 * i18next devuelve la ruta de la clave cuando falta (truthy), por eso `defaultValue`
 * es obligatorio aquí.
 */
export const formatExpenseCategory = (category, t) => {
    if (!category) return '';
    return t(`expensesPage.categories.${category}`, { defaultValue: category });
};

/**
 * Opciones para un `<select>` / `react-select`: las 10 base traducidas seguidas de las
 * personalizadas activas, evitando duplicar una que coincida con una clave base o con su
 * etiqueta traducida.
 *
 * @param {Array<{name:string,is_active?:boolean}>} customCategories
 * @param {Function} t
 * @returns {Array<{value:string,label:string,__builtin:boolean}>}
 */
export const buildExpenseCategoryOptions = (customCategories, t) => {
    const builtin = BUILTIN_EXPENSE_CATEGORY_KEYS.map((key) => ({
        value: key,
        label: formatExpenseCategory(key, t),
        __builtin: true,
    }));

    const taken = new Set(
        builtin.flatMap((o) => [o.value.toLowerCase(), o.label.toLowerCase()])
    );

    const custom = (customCategories || [])
        .filter((c) => c && c.is_active !== false && c.name && !taken.has(c.name.toLowerCase()))
        .map((c) => ({ value: c.name, label: c.name, __builtin: false }));

    return [...builtin, ...custom];
};
